import { FC, useMemo } from 'react'
import { FeedBadge, ComposeType } from 'interfaces/feed'
import sortBy from 'lodash/sortBy'
import { FeedUserBadge } from 'components/features/socialFeed/FeedUserInfo/FeedUserBadge'
import omit from 'lodash/omit'
import pick from 'lodash/pick'
import { useStoreSelector } from 'helpers/reactReduxHelpers'
import { isMemberPremiumSelector } from 'selectors/globalSelectors'

const AUTHOR_BADGE: FeedBadge = {
  slug: 'author',
  text: 'Author',
}

const PREMIUM_BADGE: FeedBadge = {
  slug: 'premium',
  text: 'Premium',
}

const BADGES_ORDER: Partial<Record<FeedBadge['slug'], number>> = {
  premium: 0,
  verified: 0,
  author: 1,
  '1000+likes': 2,
}

export type FeedUserBadgesProps = {
  badges: FeedBadge[]
  isPostAuthor?: boolean
  mode: ComposeType
  isOwn?: boolean
  className?: string
}

export const FeedUserBadges: FC<FeedUserBadgesProps> = ({
  badges,
  isPostAuthor,
  mode,
  className,
  isOwn = false,
}) => {
  const isMemberPremium = useStoreSelector(isMemberPremiumSelector)

  const shownBadges = useMemo(() => {
    // add "author" badge if needed
    const badgesWithAuthor = [
      ...badges,
      ...(mode === 'comment' && isPostAuthor ? [AUTHOR_BADGE] : []),
      ...(isOwn && isMemberPremium ? [PREMIUM_BADGE] : []),
    ]

    let badgesMap = badgesWithAuthor.reduce((acc, badge) => ({
      ...acc,
      [badge.slug]: {
        ...badge,
        sortKey: BADGES_ORDER[badge.slug] !== undefined ? BADGES_ORDER[badge.slug] : 99,
      },
    }), {} as Partial<Record<FeedBadge['slug'], FeedBadge & { sortKey: number }>>)

    if (badgesMap.premium) {
      badgesMap = omit(badgesMap, 'verified')
    }

    if (isOwn && !isMemberPremium) {
      badgesMap = omit(badgesMap, 'premium')
    }

    // show only "verified", "premium" & "author" badges for comment
    if (mode === 'comment') {
      badgesMap = pick(badgesMap, ['verified', 'premium', 'author'])
    }

    return sortBy(Object.values(badgesMap), 'sortKey') as FeedBadge[]
  }, [badges, isPostAuthor, mode, isMemberPremium, isOwn])

  const badgesChildren = useMemo(() => shownBadges.map((badge) => {
    return (
      <FeedUserBadge
        key={ badge.slug }
        badge={ badge }
        mode={ mode === 'comment' ? 'contrast' : 'default' }
        className={ className }
      />
    )
  }), [shownBadges, mode, className])

  return (
    <>
      { badgesChildren }
    </>
  )
}
